/* Homepage behaviour (v2.0 "Datum Field"): the previous/next buttons of the sideways rows (best sellers, review wall),
   the customer photo slideshow, keyboard focus in the review rows and the proof tiles' links to this page. Facts
   render in their final state (guide 5.4, 7.3). Homepage motion lives in df-motion.js (owner, 2026-10-20); while it
   runs the review wall as a tape (section.dfm-tape), this file leaves the wall's buttons to it.
   Slideshow (owner decision D7 = B): with the section's "Play automatically" on, it advances on its timer with a 180 ms
   opacity fade (CSS; no slide or zoom) only while it is on screen and the tab is visible. A visible pause/play button
   stops it for good (WCAG 2.2.2); it also pauses while a mouse is over it and after keyboard focus enters it. It never
   plays for visitors who ask for reduced motion (the button is then hidden, since nothing moves) or in the theme editor.
   Arrows, thumbnails, arrow keys and swipe always work. Loaded by every homepage section; runs once. */
(function () {
  'use strict';
  if (window.__bhmHome) return;
  window.__bhmHome = true;

  var docEl = document.documentElement;
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var design = !!(window.Shopify && window.Shopify.designMode);
  // Only the slideshow autoplay depends on this
  var motion = !reduce.matches && !design;
  function scrollMode() { return reduce.matches ? 'auto' : 'smooth'; }

  /* Sideways rows (best sellers below 1000 px, the review wall): previous and next buttons, shown only when the row
     actually scrolls. Each press moves one screenful of whole cards (at least one card). */
  function initTracks(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-bhm-track]'), function (track) {
      if (track.dataset.bhmTrack === 'ready') return;
      track.dataset.bhmTrack = 'ready';
      var section = track.closest('section') || scope;
      var wrap = section.querySelector('[data-bhm-scroll-btns]');
      var prev = section.querySelector('[data-bhm-prev]');
      var next = section.querySelector('[data-bhm-next]');
      if (!wrap || !prev || !next) return;
      function stepSize() {
        var card = track.firstElementChild;
        if (!card) return track.clientWidth;
        var cs = window.getComputedStyle(track);
        var unit = card.getBoundingClientRect().width + (parseFloat(cs.columnGap) || 0);
        var view = track.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
        return Math.max(1, Math.floor((view + 1) / unit)) * unit;
      }
      function update() {
        // df-motion.js runs the review wall as a tape (section.dfm-tape): both buttons always work there
        if (section.classList.contains('dfm-tape')) { wrap.hidden = false; prev.setAttribute('aria-disabled', 'false'); next.setAttribute('aria-disabled', 'false'); return; }
        var scrollable = track.scrollWidth > track.clientWidth + 4;
        wrap.hidden = !scrollable;
        var atStart = track.scrollLeft <= 2;
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      }
      function go(dir) { if (section.classList.contains('dfm-tape')) return; track.scrollBy({ left: dir * stepSize(), behavior: scrollMode() }); }
      prev.addEventListener('click', function () { if (prev.getAttribute('aria-disabled') !== 'true') go(-1); });
      next.addEventListener('click', function () { if (next.getAttribute('aria-disabled') !== 'true') go(1); });
      track.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
      // Tabbing through the best sellers: bring the focused card fully into view (scroll-snap would otherwise leave it half hidden).
      // Review cards are handled by initWalls.
      track.addEventListener('focusin', function (e) {
        var card = e.target.closest('.bhm-card');
        if (!card || (e.target.matches && !e.target.matches(':focus-visible'))) return;
        var t = track.getBoundingClientRect(), c = card.getBoundingClientRect();
        if (c.left < t.left || c.right > t.right) card.scrollIntoView({ inline: 'start', block: 'nearest', behavior: scrollMode() });
      });
      window.addEventListener('resize', update);
      update();
    });
  }

  // Keyboard (and assistive technology) focus, as opposed to a mouse press or a tap, which also focus a control
  function keyboardFocus(el) { try { return el.matches(':focus-visible'); } catch (err) { return true; } }
  // Runs fn if the visitor switches reduced motion on while the page is open
  function onReduce(fn) {
    var h = function (e) { if (e.matches) fn(); };
    if (reduce.addEventListener) reduce.addEventListener('change', h); else if (reduce.addListener) reduce.addListener(h);
  }

  /* ---------- Customer photo slideshow ---------- */
  // Far photos keep their address in data-src until they are about to be shown
  function loadSlide(s) {
    if (!s) return;
    Array.prototype.forEach.call(s.querySelectorAll('img[data-src]'), function (img) { img.src = img.getAttribute('data-src'); img.removeAttribute('data-src'); });
  }
  function initShows(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-bhm-show]'), function (show) {
      if (show.dataset.bhmShowReady) return;
      var slides = Array.prototype.slice.call(show.querySelectorAll('[data-bhm-slide]'));
      if (slides.length < 2) return;
      show.dataset.bhmShowReady = '1';
      var count = slides.length;
      var interval = Math.max(4000, Number(show.getAttribute('data-interval')) || 6000);
      var wantAuto = show.getAttribute('data-autoplay') === 'true' && motion;
      var prev = show.querySelector('[data-bhm-show-prev]'), next = show.querySelector('[data-bhm-show-next]');
      var toggle = show.querySelector('[data-bhm-show-toggle]'), label = toggle && toggle.querySelector('[data-bhm-toggle-label]');
      var current = show.querySelector('[data-bhm-current]'), status = show.querySelector('[data-bhm-show-status]');
      var thumbsWrap = show.querySelector('[data-bhm-thumbs]');
      var stage = show.querySelector('.bhm-show__stage');
      var index = 0, timer = null, userPaused = !wantAuto, hovered = false, visible = false;
      var remaining = interval, startedAt = 0; // time left on the current photo, so a pause resumes where it stopped
      // Thumbnails use the small Loox copy (data-thumb); loading is set before src, or the browser fetches at once
      var thumbs = slides.map(function (s, i) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'bhm-thumb';
        b.setAttribute('aria-label', 'Show photo ' + (i + 1) + ' of ' + count);
        var img = s.querySelector('img[src]');
        var src = s.getAttribute('data-thumb') || (img && img.getAttribute('src'));
        if (src) { var t = document.createElement('img'); t.alt = ''; t.loading = 'lazy'; t.decoding = 'async'; t.src = src; b.appendChild(t); }
        b.addEventListener('click', function () { userPaused = true; syncToggle(); go(i, true); });
        if (thumbsWrap) thumbsWrap.appendChild(b);
        return b;
      });
      // Every photo but the first waits in data-src; go() loads the one shown and its neighbours (still lazy until near the screen)
      slides.forEach(function (s, k) {
        if (k === 0) return;
        Array.prototype.forEach.call(s.querySelectorAll('img[src]'), function (img) { img.setAttribute('data-src', img.getAttribute('src')); img.removeAttribute('src'); });
      });
      show.classList.add('is-ready');
      function pad(n) { return (n < 10 ? '0' : '') + n; }
      function playing() { return wantAuto && !userPaused && !hovered && visible && !document.hidden; }
      function schedule() {
        clearTimeout(timer); timer = null;
        if (startedAt) { remaining -= window.performance.now() - startedAt; startedAt = 0; }
        var on = playing();
        show.classList.toggle('is-playing', on);
        if (on) { startedAt = window.performance.now(); timer = setTimeout(function () { go(index + 1, false); }, Math.max(0, remaining)); }
      }
      function go(i, announce) {
        index = (i + count) % count;
        loadSlide(slides[index]); loadSlide(slides[(index + 1) % count]); loadSlide(slides[(index - 1 + count) % count]);
        slides.forEach(function (s, k) {
          var on = k === index;
          s.classList.toggle('is-active', on);
          if (on) { s.removeAttribute('aria-hidden'); s.removeAttribute('inert'); } else { s.setAttribute('aria-hidden', 'true'); s.setAttribute('inert', ''); }
        });
        thumbs.forEach(function (b, k) { if (k === index) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current'); });
        // Automatic advances jump the thumbnail strip; only a visitor's own action may glide it
        var tb = thumbs[index];
        if (tb && thumbsWrap) { var l = tb.offsetLeft - thumbsWrap.clientWidth / 2 + tb.offsetWidth / 2; thumbsWrap.scrollTo({ left: l, behavior: announce ? scrollMode() : 'auto' }); }
        if (current) current.textContent = pad(index + 1);
        // Only a visitor's action is announced; timed advances stay silent so screen readers aren't interrupted
        if (announce && status) status.textContent = 'Photo ' + (index + 1) + ' of ' + count;
        remaining = interval; startedAt = 0;
        schedule();
      }
      // The label says what a press does; the icon follows data-state
      function syncToggle() {
        if (toggle) {
          toggle.setAttribute('data-state', userPaused ? 'paused' : 'playing');
          if (label) label.textContent = userPaused ? 'Play slideshow' : 'Pause slideshow';
        }
        schedule();
      }
      // No rotation control unless the slideshow can actually play on its own (reduced motion, theme editor, autoplay off)
      if (toggle) toggle.hidden = !wantAuto;
      if (prev) prev.addEventListener('click', function () { userPaused = true; syncToggle(); go(index - 1, true); });
      if (next) next.addEventListener('click', function () { userPaused = true; syncToggle(); go(index + 1, true); });
      // An explicit Play wins over the hover pause, so it plays even with the pointer still on the slideshow
      if (toggle) toggle.addEventListener('click', function () { userPaused = !userPaused; if (!userPaused) hovered = false; syncToggle(); });
      show.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { hovered = true; schedule(); } });
      show.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { hovered = false; schedule(); } });
      // Keyboard focus from outside, or on a photo's link or a thumbnail, stops the rotation until the visitor presses Play
      // (WAI-ARIA carousel pattern). After Play, moving between the buttons keeps it playing; a focused link is never made inert.
      show.addEventListener('focusin', function (e) {
        if (userPaused || !keyboardFocus(e.target)) return;
        if (show.contains(e.relatedTarget) && e.target.closest('.bhm-show__controls')) return;
        userPaused = true; syncToggle();
      });
      // Arrow keys change the photo; focus on the old photo's link moves to the new one, so it isn't lost when the old slide goes inert.
      // Arrows with Alt, Ctrl, Shift or Meta are left to the browser (e.g. Alt+Left is Back).
      show.addEventListener('keydown', function (e) {
        if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        var fromSlide = slides[index].contains(document.activeElement);
        userPaused = true; syncToggle();
        go(index + (e.key === 'ArrowRight' ? 1 : -1), true);
        if (fromSlide) { var a = slides[index].querySelector('a'); if (a) a.focus(); }
      });
      // Swipe: touch-action pan-y lets the browser keep vertical scrolling and hand sideways moves to us
      var x0 = null;
      if (stage) {
        stage.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse') x0 = e.clientX; });
        stage.addEventListener('pointercancel', function () { x0 = null; });
        stage.addEventListener('pointerup', function (e) {
          if (x0 === null) return; var dx = e.clientX - x0; x0 = null;
          if (Math.abs(dx) > 40) { userPaused = true; syncToggle(); go(index + (dx < 0 ? 1 : -1), true); }
        });
      }
      if (wantAuto) {
        document.addEventListener('visibilitychange', schedule);
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (es) { visible = es[es.length - 1].isIntersecting; schedule(); }, { threshold: 0.35 }).observe(show);
        } else { visible = true; }
        // Reduced motion switched on while the page is open: stop for good and drop the control
        onReduce(function () { wantAuto = false; userPaused = true; if (toggle) toggle.hidden = true; syncToggle(); });
      }
      syncToggle();
      go(0, false);
    });
  }

  /* ---------- Review rows: scrolled by hand; keyboard focus brings the whole card into view ---------- */
  // The browser doesn't do this for a card that is partly visible, sideways in its row or under the fixed header.
  // Mouse presses and taps are left alone, so a click on a product link still lands on it. Coming back to the tab or
  // window fires focusin again on the link that already had focus: that one is left alone.
  function initWalls(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-bhm-wall]'), function (wall) {
      if (wall.dataset.bhmWallReady) return;
      wall.dataset.bhmWallReady = '1';
      var back = null;
      window.addEventListener('blur', function () { back = document.activeElement; });
      window.addEventListener('focus', function () { setTimeout(function () { back = null; }, 0); });
      wall.addEventListener('focusin', function (e) {
        if (e.target === back || !keyboardFocus(e.target)) return;
        var card = e.target.closest('.bhm-wall__card');
        var row = card && card.closest('[data-bhm-marquee]');
        if (!row) return;
        var pad = parseFloat(window.getComputedStyle(docEl).scrollPaddingTop) || 0;
        var rr = row.getBoundingClientRect(), cr = card.getBoundingClientRect();
        if (cr.left < rr.left || cr.right > rr.right || cr.top < pad - 1 || cr.bottom > window.innerHeight + 1) card.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    });
  }

  /* ---------- Proof tiles: links to a section of this page ---------- */
  // The theme's own "#" link scroller (theme.js) is kept out, so the browser's fragment navigation runs, which honours
  // the header offset (html scroll-padding-top) and moves the Tab start point there. A link whose section isn't shown
  // (e.g. no customer photos left) is dropped, with its Plus Line wrapper, and so is a links row left empty.
  function initProof(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('.bhm-hud__links a[href^="#"]'), function (a) {
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      if (document.getElementById(id)) {
        if (!a.dataset.bhmAnchor) { a.dataset.bhmAnchor = '1'; a.addEventListener('click', function (e) { e.stopPropagation(); }); }
        return;
      }
      var links = a.closest('.bhm-hud__links');
      var wrap = a.parentNode !== links ? a.parentNode : a;
      wrap.parentNode.removeChild(wrap);
      if (links && !links.querySelector('a') && links.parentNode) links.parentNode.removeChild(links);
    });
  }

  // Each part runs on its own, so an error in one never stops the others
  function init(scope) {
    [initTracks, initShows, initWalls, initProof].forEach(function (fn) {
      try { fn(scope); } catch (err) { if (window.console) window.console.error('brand-home.js', err); }
    });
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
})();
